import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import IntakeForm from "./IntakeForm";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DoctorIntakeForm - Smart Link Entry Point
 *
 * Resolves doctor from URL slugs (/:institutionSlug/:doctorSlug)
 * and renders IntakeForm directly with doctor/institution context.
 */
const DoctorIntakeForm = () => {
  const { institutionSlug, doctorSlug } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [doctorData, setDoctorData] = useState(null);

  useEffect(() => {
    const resolveSmartLink = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/doctors/${institutionSlug}/${doctorSlug}`
        );

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.detail || { error: "unknown", message: "Error desconocido" });
          return;
        }

        const data = await response.json();
        setDoctorData(data);
      } catch (err) {
        console.error("Error resolving smart link:", err);
        setError({ error: "network", message: "Error de conexión. Por favor, intenta nuevamente." });
      } finally {
        setLoading(false);
      }
    };

    resolveSmartLink();
  }, [institutionSlug, doctorSlug]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center" data-testid="smart-link-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando información del médico...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center p-4" data-testid="smart-link-error">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {error.error === "doctor_not_found" && "Médico no encontrado"}
            {error.error === "institution_not_found" && "Institución no encontrada"}
            {error.error === "doctor_inactive" && "Médico no disponible"}
            {error.error === "doctor_not_accepting" && "Agenda completa"}
            {error.error === "institution_inactive" && "Institución no disponible"}
            {error.error === "network" && "Error de conexión"}
            {!["doctor_not_found", "institution_not_found", "doctor_inactive", "doctor_not_accepting", "institution_inactive", "network"].includes(error.error) && "Error"}
          </h1>

          <p className="text-slate-600 mb-6">
            {error.message}
          </p>

          <div className="space-y-3">
            <Button
              onClick={() => navigate("/")}
              className="w-full"
              data-testid="smart-link-error-home-btn"
            >
              Ir al inicio
            </Button>
            <p className="text-sm text-slate-500">
              Si crees que esto es un error, contacta a tu médico o institución.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render intake form directly (no intermediate landing screen)
  return (
    <IntakeForm
      doctor={doctorData.doctor}
      institution={doctorData.institution}
      source="direct_link"
      sourceSlug={`${institutionSlug}/${doctorSlug}`}
      onCancel={() => navigate("/")}
    />
  );
};

export default DoctorIntakeForm;
